/* ═══════════════════════════════════════════════════════════════
   Runic Printing — Theme JavaScript
   ═══════════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  /* ── Mobile menu toggle ─────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-menu-toggle]');
    if (!btn) return;
    const panel = document.getElementById(btn.getAttribute('data-menu-toggle'));
    if (!panel) return;
    panel.toggleAttribute('hidden');
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const panel = document.getElementById('mobileMenu');
      if (panel && !panel.hasAttribute('hidden')) panel.setAttribute('hidden', '');
      // Close cart drawer
      closeCartDrawer();
    }
  });

  /* ── Fade-in sections on scroll ─────────────────────────── */
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = 'running';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });

    document.querySelectorAll('.section').forEach(s => {
      s.style.animationPlayState = 'paused';
      observer.observe(s);
    });
  }

  /* ── Product Image Gallery ──────────────────────────────── */
  document.querySelectorAll('[data-gallery-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const gallery = thumb.closest('[data-product-gallery]');
      if (!gallery) return;
      const mainImg = gallery.querySelector('[data-gallery-main] img');
      if (!mainImg) return;

      // Update main image
      mainImg.src = thumb.dataset.galleryThumb;
      mainImg.srcset = thumb.dataset.gallerySrcset || '';

      // Update active state
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  /* ── Quantity Selectors ─────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const input = btn.parentElement.querySelector('input[type="number"]');
    if (!input) return;
    const current = parseInt(input.value) || 1;
    const delta = parseInt(btn.dataset.qtyChange);
    const newVal = Math.max(1, current + delta);
    input.value = newVal;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ── Variant Selector (option-based) ────────────────────── */
  const variantSelectors = document.querySelectorAll('[data-variant-select]');
  variantSelectors.forEach(select => {
    select.addEventListener('change', () => {
      const form = select.closest('form');
      if (!form) return;

      // Find matching variant from the product JSON
      const productJson = form.querySelector('[data-product-json]');
      if (!productJson) return;
      const product = JSON.parse(productJson.textContent);

      // Get all option values
      const options = [];
      form.querySelectorAll('[data-variant-select]').forEach(s => {
        options.push(s.value);
      });

      // Find matching variant
      const variant = product.variants.find(v => {
        return v.options.every((opt, i) => opt === options[i]);
      });

      if (variant) {
        // Update hidden variant id
        const idInput = form.querySelector('input[name="id"], select[name="id"]');
        if (idInput) idInput.value = variant.id;

        // Update price display
        const priceEl = form.closest('.section')?.querySelector('[data-product-price]');
        if (priceEl) {
          priceEl.textContent = formatMoney(variant.price);
        }

        // Update compare price
        const compareEl = form.closest('.section')?.querySelector('[data-compare-price]');
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }

        // Update add-to-cart button
        const addBtn = form.querySelector('[data-add-to-cart]');
        if (addBtn) {
          if (variant.available) {
            addBtn.disabled = false;
            addBtn.textContent = addBtn.dataset.addLabel || 'Add to cart';
          } else {
            addBtn.disabled = true;
            addBtn.textContent = addBtn.dataset.soldLabel || 'Sold out';
          }
        }

        // Update URL without reload
        if (history.replaceState) {
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }

        // Update gallery image if variant has image
        if (variant.featured_image) {
          const gallery = document.querySelector('[data-product-gallery]');
          if (gallery) {
            const mainImg = gallery.querySelector('[data-gallery-main] img');
            if (mainImg) {
              mainImg.src = variant.featured_image.src;
            }
          }
        }
      }
    });
  });

  /* ── AJAX Add to Cart ───────────────────────────────────── */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();

    const addBtn = form.querySelector('[data-add-to-cart]');
    const originalText = addBtn?.textContent;
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Add to cart failed');

      const item = await res.json();

      // Show notification
      showCartNotification(item.title || 'Item added to cart');

      // Update cart count in header
      updateCartCount();

      // Optionally open cart drawer
      if (document.querySelector('.cart-drawer')) {
        await refreshCartDrawer();
        openCartDrawer();
      }

    } catch (err) {
      console.error('Cart error:', err);
      showCartNotification('Could not add to cart. Please try again.');
    } finally {
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.textContent = originalText;
      }
    }
  });

  /* ── Cart Notification Toast ────────────────────────────── */
  function showCartNotification(message) {
    let el = document.querySelector('.cart-notification');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cart-notification';
      document.body.appendChild(el);
    }
    el.textContent = '✓ ' + message;
    el.classList.add('is-visible');
    clearTimeout(el._timeout);
    el._timeout = setTimeout(() => el.classList.remove('is-visible'), 3000);
  }

  /* ── Cart Count ─────────────────────────────────────────── */
  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
    } catch (e) { /* fail silently */ }
  }

  /* ── Cart Drawer ────────────────────────────────────────── */
  function openCartDrawer() {
    document.querySelector('.cart-drawer-overlay')?.classList.add('is-open');
    document.querySelector('.cart-drawer')?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    document.querySelector('.cart-drawer-overlay')?.classList.remove('is-open');
    document.querySelector('.cart-drawer')?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  // Cart drawer triggers
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-cart]')) {
      e.preventDefault();
      refreshCartDrawer().then(openCartDrawer);
    }
    if (e.target.closest('.cart-drawer-overlay') || e.target.closest('[data-close-cart]')) {
      closeCartDrawer();
    }
  });

  // Cart drawer item removal
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-remove-item]');
    if (!btn) return;
    e.preventDefault();
    const key = btn.dataset.removeItem;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      await refreshCartDrawer();
      updateCartCount();
    } catch (err) { console.error(err); }
  });

  // Cart drawer quantity change
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('.cart-drawer [data-item-qty]');
    if (!input) return;
    const key = input.dataset.itemQty;
    const qty = parseInt(input.value) || 0;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      await refreshCartDrawer();
      updateCartCount();
    } catch (err) { console.error(err); }
  });

  async function refreshCartDrawer() {
    const drawer = document.querySelector('.cart-drawer');
    if (!drawer) return;
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const itemsContainer = drawer.querySelector('.cart-drawer__items');
      const footerContainer = drawer.querySelector('.cart-drawer__footer');

      if (cart.item_count === 0) {
        itemsContainer.innerHTML = '<div class="cart-drawer__empty"><div style="font-size:28px;margin-bottom:8px;">🛒</div><p>Your cart is empty</p></div>';
        footerContainer.innerHTML = '';
        return;
      }

      let itemsHtml = '';
      cart.items.forEach(item => {
        const imgSrc = item.image ? item.image.replace(/(\.[^.]+)$/, '_200x200$1') : '';
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        itemsHtml += `
          <div class="cart-drawer__item">
            ${imgSrc ? `<div class="cart-drawer__item-img"><img src="${imgSrc}" alt="${item.title}" loading="lazy"></div>` : ''}
            <div class="cart-drawer__item-details">
              <p class="cart-drawer__item-title">${item.title}</p>
              ${variantTitle ? `<p class="cart-drawer__item-variant">${variantTitle}</p>` : ''}
              <p class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</p>
              <div class="qty-selector" style="margin-top:6px;">
                <button type="button" data-qty-change="-1" aria-label="Decrease">−</button>
                <input type="number" value="${item.quantity}" min="0" data-item-qty="${item.key}" aria-label="Quantity">
                <button type="button" data-qty-change="1" aria-label="Increase">+</button>
              </div>
            </div>
            <button data-remove-item="${item.key}" aria-label="Remove" style="background:none;border:none;color:var(--color-muted);cursor:pointer;font-size:16px;padding:4px;">✕</button>
          </div>
        `;
      });
      itemsContainer.innerHTML = itemsHtml;

      footerContainer.innerHTML = `
        <div class="cart-drawer__total">
          <span style="color:var(--color-muted);">Subtotal</span>
          <strong style="color:var(--color-accent);">${formatMoney(cart.total_price)}</strong>
        </div>
        <a class="btn btn--primary" href="/checkout" style="width:100%;margin-bottom:6px;">Checkout</a>
        <a class="btn" href="/cart" style="width:100%;font-size:13px;">View Cart</a>
      `;
    } catch (err) { console.error(err); }
  }

  /* ── Money formatter ────────────────────────────────────── */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents.replace(/[^0-9]/g, ''));
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── Predictive search (lightweight) ────────────────────── */
  const searchInputs = document.querySelectorAll('[data-predictive-search]');
  searchInputs.forEach(input => {
    let timeout;
    const resultsContainer = document.createElement('div');
    resultsContainer.className = 'predictive-search-results';
    resultsContainer.style.cssText = 'position:absolute;top:100%;left:0;right:0;z-index:60;background:var(--color-card);border:1px solid var(--color-border);border-radius:var(--radius-sm);box-shadow:var(--shadow);max-height:320px;overflow-y:auto;display:none;';
    input.parentElement.style.position = 'relative';
    input.parentElement.appendChild(resultsContainer);

    input.addEventListener('input', () => {
      clearTimeout(timeout);
      const q = input.value.trim();
      if (q.length < 2) { resultsContainer.style.display = 'none'; return; }

      timeout = setTimeout(async () => {
        try {
          const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,article,page&resources[limit]=6`);
          const data = await res.json();
          const resources = data.resources?.results || {};
          let html = '';

          ['products', 'articles', 'pages'].forEach(type => {
            if (resources[type]?.length) {
              resources[type].forEach(item => {
                const img = item.image || item.featured_image?.url || '';
                html += `<a href="${item.url}" style="display:flex;align-items:center;gap:10px;padding:10px 14px;border-bottom:1px solid var(--color-border);font-size:14px;">
                  ${img ? `<img src="${typeof img === 'string' ? img.replace(/(\.[^.]+)$/, '_80x80$1') : img}" style="width:36px;height:36px;object-fit:cover;border-radius:6px;" alt="">` : ''}
                  <span>${item.title}</span>
                </a>`;
              });
            }
          });

          if (!html) html = '<div style="padding:14px;color:var(--color-muted);font-size:14px;text-align:center;">No results</div>';
          html += `<a href="/search?q=${encodeURIComponent(q)}" style="display:block;padding:10px 14px;text-align:center;color:var(--color-accent);font-size:13px;font-weight:600;">View all results →</a>`;

          resultsContainer.innerHTML = html;
          resultsContainer.style.display = 'block';
        } catch (e) { /* fail silently */ }
      }, 300);
    });

    // Hide results on blur
    document.addEventListener('click', (e) => {
      if (!input.contains(e.target) && !resultsContainer.contains(e.target)) {
        resultsContainer.style.display = 'none';
      }
    });
  });

  /* ── Init cart count on page load ───────────────────────── */
  updateCartCount();

})();
